
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ButtonSwitch from '@/components/global/ButtonSwitch.vue';
import CountryRepository from '@/repositories/CountryRepository';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import ClientInformation from '@/models/ClientInformation';
import ClientInformationPopup from '@/components/global/client/ClientInformationPopup.vue';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import ClientLogoAttachments from '@/components/global/client/ClientLogoAttachments.vue';
import ClientAddressForm from '@/components/views/ClientView/ClientAddressForm.vue';
import { vatCountryCodes } from '@/regex/taxData';
import ClientAddressCarousel from '@/components/global/client/ClientAddressCarousel.vue';
import CustomerTypeSection from '@/components/global/client/CustomerTypeSection.vue';
import TaxesPaymentSection from '@/components/global/client/TaxesPaymentSection.vue';
import AddressConfig from '@/models/interfaces/Address';

@Component({
    name: 'CustomerInformation',
    components: {
        ButtonSwitch,
        ClientInformationPopup,
        ClientLogoAttachments,
        ClientAddressForm,
        ClientAddressCarousel,
        CustomerTypeSection,
        TaxesPaymentSection,
    },
})
export default class CustomerInformation extends Vue {
    @Prop({ required: true }) private value!: any;
    @Prop({ default: false }) private isNewClient!: boolean;
    @Prop({ default: null }) private clientId!: string;
    @Prop({ default: () => ({}) }) private invoiceEmailErrors!: any;
    @Prop({ required: true }) private isItalyUG!: boolean;
    @Prop({ default: null }) private defaultCountryId!: any;
    @Prop({ default: () => [] }) private addressTypes!: any[];
    @Prop({ required: true }) private canAccessLogistics!: boolean;

    private loadingOverlay = new LoadingOverlayHelper(this, {});
    private showClientInformationPopup = false;
    private clientInformation: ClientInformation | null = null;
    private vatCountryCodes = vatCountryCodes;

    private selectedCountry = this.defaultCountry ? this.defaultCountry.id + '|' + this.defaultCountry.code : '';

    private get defaultCountry(): any {
        if (!this.value) {
            return;
        }

        // Check first address for country if clientAddresses exists
        if (
            this.value.clientAddresses &&
            this.value.clientAddresses.length > 0 &&
            this.value.clientAddresses[0].country
        ) {
            return CountryRepository.getById(this.value.clientAddresses[0].country);
        }

        // Fallback to addressForm for backward compatibility
        if (this.value.addressForm && this.value.addressForm.country) {
            return CountryRepository.getById(this.value.addressForm.country);
        }

        return '';
    }

    private get isRoltek(): boolean {
        const userGroup = String(this.$store.getters['jwtData/currentUserGroup']);

        if (userGroup === '1' || userGroup === '2') {
            return true;
        }

        return false;
    }

    private emptyAddress() {
        return {
            id: null,
            _localId: `_temp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
            streetNameAndNumber: null,
            poNumber: null,
            city: null,
            building: null,
            country: this.defaultCountryId || null,
            addressType: null,
            isDefault: false,
        };
    }

    private addAddress() {
        if (!this.value.clientAddresses) {
            this.$set(this.value, 'clientAddresses', []);
        }

        this.value.clientAddresses.push(this.emptyAddress());
    }

    private removeAddress(index: number, address: AddressConfig) {
        if (address.isDefault) {
            this.$notification.error({
                message: this.$t('Upozorenje') as string,
                description: this.$t('You cannot delete default address') as string,
            });

            return;
        }

        this.value.clientAddresses.splice(index, 1);
        this.$set(this.value, 'clientAddresses', this.value.clientAddresses);
    }

    private updateAddress(index: number, updatedAddress: AddressConfig) {
        this.$set(this.value.clientAddresses, index, updatedAddress);
    }

    public validateAll(): boolean {
        const taxesSection = this.$refs.taxesPaymentSection as any;
        if (taxesSection && typeof taxesSection.validateSendingProfile === 'function') {
            return taxesSection.validateSendingProfile();
        }
        return true;
    }

    private onDefaultAddressChange(updatedAddress: AddressConfig, isDefault: boolean) {
        if (updatedAddress.isDefault === true) {
            this.$notification.error({
                message: this.$t('Upozorenje') as string,
                description: this.$t('You need to have at least one default address') as string,
            });

            return;
        }

        this.value.clientAddresses.forEach((address: AddressConfig) => {
            address.isDefault = false;
        });

        updatedAddress.isDefault = true;

        this.$set(this.value, 'clientAddresses', this.value.clientAddresses);
    }

    private get isRoltekUG() {
        return this.$store.getters['jwtData/isRoltekUserGroup'];
    }

    private async getClientInformation() {
        if (!this.value.taxId) {
            this.$notification.destroy();
            this.$notification.error({
                message: 'Error',
                description: 'Please enter tax ID.',
            });
            return;
        }

        let clientInformation;
        this.loadingOverlay.start();

        const formattedTaxId = this.formatTaxId(this.value.taxId);

        try {
            clientInformation = await ClientInformation.getById(formattedTaxId);
        } catch (e) {
            this.showErrorAndResetClientInformationForm(
                clientInformation?.data?.message ?? this.$t('Getting client information failed.'),
            );
            return;
        } finally {
            this.loadingOverlay.stop();
        }

        if (!clientInformation) {
            this.showErrorAndResetClientInformationForm(this.$t('Getting client information failed.'));
            return;
        }

        if (clientInformation?.data?.message) {
            this.showErrorAndResetClientInformationForm(
                clientInformation?.data?.message ?? this.$t('Getting client information failed.'),
            );
            return;
        }

        this.clientInformation = clientInformation.data;
        this.showClientInformationPopup = true;
    }

    private formatTaxId(taxId: string) {
        const prefix = parseInt(taxId.substring(0, 2), 10);
        if (isNaN(prefix)) {
            return taxId.substring(2);
        }

        return taxId;
    }

    private showErrorAndResetClientInformationForm(message: string) {
        this.$notification.error({
            message: this.$t('Error'),
            description: message,
        });
        this.clientInformation = null;
    }

    private handleClientInformationConfirmation() {
        EventBus.$emit(EventBusEvents.clientInformationConfirmed);
        this.showClientInformationPopup = false;
    }

    private handleClientInformationCancelation() {
        EventBus.$emit(EventBusEvents.clientInformationCanceled);
        this.showClientInformationPopup = false;
    }

    private setDefaultTaxCountryCode() {
        if (this.value.isTaxPayer && this.value.isCompany) {
            return;
        }

        if (this.isRoltekUG) {
            this.value.taxCountryPrefix = 'SI';

            return;
        }

        if (this.defaultCountry) {
            const validVatCountry = this.vatCountryCodes.find((country) => country.code === this.defaultCountry?.code);

            if (!validVatCountry) {
                this.value.taxCountryPrefix = 'SI';

                return;
            }

            this.value.taxCountryPrefix = this.defaultCountry.code;
        }
    }

    private created() {
        EventBus.$on(EventBusEvents.getClientInformation, this.getClientInformation);
    }

    private mounted() {
        this.setDefaultTaxCountryCode();
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.getClientInformation, this.getClientInformation);
    }

    @Watch('value', {
        immediate: true,
        deep: true,
    })
    private onValueChange() {
        this.$emit('input', this.value);
    }
}
